import React, { useEffect, useState } from 'react';
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { fetchSOAPNoteResponse } from './api/openai';

export default function TemplateBank({ onSelect }) {
    const [templates, setTemplates] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [aiOutput, setAiOutput] = useState('');
    const [aiStatus, setAiStatus] = useState('idle');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [newTemplate, setNewTemplate] = useState({ title: '', content: '', category: '' });

    useEffect(() => {
        const fetchTemplates = async () => {
            const snapshot = await getDocs(collection(db, 'templateBank'));
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTemplates(docs);
        };
        fetchTemplates();
    }, []);

    const filteredTemplates = templates.filter(t =>
        (selectedCategory === 'All' || t.category === selectedCategory) &&
        (t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.content.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handlePrompt = async () => {
        if (!searchTerm.trim()) return;
        setAiStatus('loading');
        try {
            const result = await fetchSOAPNoteResponse(searchTerm, selectedCategory);
            setAiOutput(result);
            setAiStatus('idle');
        } catch (err) {
            setAiOutput('');
            setAiStatus('error');
        }
    };

    const handleInsert = (text) => {
        if (onSelect) onSelect(text);
    };

    const handleAddTemplate = async () => {
        const docRef = await addDoc(collection(db, 'templateBank'), newTemplate);
        setTemplates(prev => [...prev, { id: docRef.id, ...newTemplate }]);
        setNewTemplate({ title: '', content: '', category: '' });
    };

    const handleDeleteTemplate = async (id) => {
        await deleteDoc(doc(db, 'templateBank', id));
        setTemplates(prev => prev.filter(t => t.id !== id));
    };

    const handleUpdateTemplate = async (id, updatedData) => {
        await updateDoc(doc(db, 'templateBank', id), updatedData);
        setTemplates(prev => prev.map(t => (t.id === id ? { ...t, ...updatedData } : t)));
    };

    return (
        <div className="p-6 space-y-4 bg-white rounded shadow max-w-5xl mx-auto max-h-[90vh] overflow-y-auto">
            {/* Search bar */}
            <input
                type="text"
                placeholder="🧠 Type here to ask AI or search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePrompt()}
                className="w-full px-4 py-2 border rounded"
            />

            {/* Category filter */}
            <div className="flex flex-wrap gap-2 mt-2">
                {['All', 'Subjective', 'Objective', 'Assessment', 'Plan', 'Intervention', 'Evaluation', 'Revision'].map(cat => (
                    <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-3 py-1 rounded border ${selectedCategory === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Admin: Add template */}
            <div className="border p-4 rounded shadow">
                <h3 className="text-lg font-semibold mb-2">➕ Add New Template</h3>
                <input
                    type="text"
                    placeholder="Title"
                    value={newTemplate.title}
                    onChange={e => setNewTemplate({ ...newTemplate, title: e.target.value })}
                    className="w-full mb-2 px-3 py-1 border rounded"
                />
                <input
                    type="text"
                    placeholder="Category"
                    value={newTemplate.category}
                    onChange={e => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full mb-2 px-3 py-1 border rounded"
                />
                <textarea
                    placeholder="Content"
                    value={newTemplate.content}
                    onChange={e => setNewTemplate({ ...newTemplate, content: e.target.value })}
                    className="w-full mb-2 px-3 py-2 border rounded"
                />
                <button
                    onClick={handleAddTemplate}
                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                >
                    ✅ Save Template
                </button>
            </div>

            {/* Templates List + AI Output Panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Left Panel */}
                <div className="space-y-3">
                    {filteredTemplates.map(t => (
                        <div key={t.id} className="border p-3 rounded shadow">
                            <div className="font-semibold">{t.title}</div>
                            <div className="text-sm text-gray-600 mb-2 line-clamp-3">{t.content}</div>
                            <div className="flex justify-end gap-2 flex-wrap">
                                <button
                                    onClick={() => setSelectedTemplate(t)}
                                    className="px-3 py-1 text-sm bg-gray-300 rounded hover:bg-gray-400"
                                >
                                    Preview
                                </button>
                                <button
                                    onClick={() => handleInsert(t.content)}
                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Add to Note
                                </button>
                                <button
                                    onClick={() => handleDeleteTemplate(t.id)}
                                    className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Right Panel */}
                <div className="bg-gray-100 p-4 rounded shadow min-h-[200px] max-h-[75vh] overflow-y-auto scrollbar-gutter-stable">
                    <h3 className="text-lg font-bold mb-2">🤖 AI Output</h3>
                    {aiStatus === 'loading' && <p className="text-sm text-gray-500">Loading response...</p>}
                    {aiStatus === 'error' && <p className="text-sm text-red-500">❌ No response from AI</p>}
                    {aiStatus === 'idle' && aiOutput && (
                        <>
                            <p className="text-sm whitespace-pre-line mb-4">{aiOutput}</p>
                            <button
                                onClick={() => handleInsert(aiOutput)}
                                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                            >
                                📋 Copy to Note
                            </button>
                        </>
                    )}

                    {selectedTemplate && (
                        <div className="mt-6">
                            <h4 className="text-md font-semibold mb-2">👁️ Previewing: {selectedTemplate.title}</h4>
                            <p className="text-sm whitespace-pre-line mb-2">{selectedTemplate.content}</p>
                            <button
                                onClick={() => setSelectedTemplate(null)}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                Close Preview
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
